/**
 * SectionNoticesDialog — "Section quality notes", opened on demand from the
 * report header's ⋮ menu.
 *
 * 2026-10-13 (William: "i dont want any banners on report pages"). The
 * quarantine / withheld / meta-leak notes no longer render inside report
 * sections on the page (`inSectionNotices`). Everything they offered stays
 * reachable here: which sections, why, the quarantined original AI output
 * (formerly "Show original AI output"), and the Author / Re-run section
 * actions the old notes carried. A dialog the reader opens, never a band
 * across the page.
 */
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { describeSectionNotice, type ReportSectionNotice } from './reportSectionNotices';

export interface SectionNoticesDialogProps {
  notices: readonly ReportSectionNotice[];
  onClose: () => void;
  /** Enter the report's edit mode (the old notes' "Author" action). */
  onAuthor?: (sectionId: string) => void;
  /** Re-run one section (the old notes' "Re-run section" / Regenerate). */
  onRerunSection?: (sectionId: string) => void;
}

export function SectionNoticesDialog({
  notices,
  onClose,
  onAuthor,
  onRerunSection,
}: SectionNoticesDialogProps): React.ReactElement {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="lg" data-testid="section-notices-dialog">
        <DialogHeader>
          <DialogTitle>Section quality notes</DialogTitle>
          <DialogDescription>
            Sections whose narrative was withheld, quarantined or flagged by an automated quality check.
            The delivered PDF keeps its notes in place.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-3">
          {notices.map((notice) => {
            const key = `${notice.kind}-${notice.sectionId}`;
            const canAuthor = Boolean(onAuthor) && notice.authorable;
            const canRerun = Boolean(onRerunSection) && notice.rerunnable;
            return (
              <li
                key={key}
                data-testid={`section-notice-${notice.sectionId}`}
                data-kind={notice.kind}
                className="rounded-lg border border-border bg-surface-1 px-3 py-2.5"
              >
                <p className="text-sm font-semibold text-text-primary">{notice.label}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-text-secondary">{describeSectionNotice(notice)}</p>
                {notice.quarantinedOriginal ? (
                  <details className="mt-1.5 text-xs" data-testid={`section-notice-original-${notice.sectionId}`}>
                    <summary className="cursor-pointer text-text-secondary">Show original AI output</summary>
                    <p className="mt-1 whitespace-pre-wrap italic text-text-muted">{notice.quarantinedOriginal}</p>
                  </details>
                ) : null}
                {canAuthor || canRerun ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {canAuthor ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        data-testid={`section-notice-author-${notice.sectionId}`}
                        onClick={() => { onAuthor?.(notice.sectionId); onClose(); }}
                      >
                        Author
                      </Button>
                    ) : null}
                    {canRerun ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        data-testid={`section-notice-rerun-${notice.sectionId}`}
                        onClick={() => { onRerunSection?.(notice.sectionId); onClose(); }}
                      >
                        Re-run section
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
